import { Minus, Plus } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { getUiLocale, useUiText } from "../lib/locale";
import { formatDecimal, parseDecimal } from "../lib/number";
import { Input, type InputProps } from "./input";

type Base = Omit<InputProps, "value" | "onChange" | "type" | "min" | "max" | "step" | "defaultValue">;

export type NumberInputProps = Base & {
  value: number | null;
  onValueChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Знаков после запятой (0 — только целые). */
  precision?: number;
  /** Кнопки −/+ по краям (количество в POS, склад). */
  stepper?: boolean;
};

const clamp = (n: number, min?: number, max?: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
const round = (n: number, p: number) => Math.round(n * 10 ** p) / 10 ** p;

export function NumberInput({ value, onValueChange, min, max, step = 1, precision = 0, stepper, onBlur, className, size, ...props }: NumberInputProps) {
  const [text, setText] = useState(formatDecimal(value, precision));
  const [focused, setFocused] = useState(false);
  const ui = useUiText();
  useEffect(() => {
    if (!focused) setText(formatDecimal(value, precision));
  }, [value, precision, focused]);

  const commit = (n: number | null) => onValueChange(n === null ? null : clamp(round(n, precision), min, max));
  const bump = (dir: 1 | -1) => commit((value ?? 0) + dir * step);
  const pattern = precision > 0 ? /^-?[\d\s ]*[.,]?\d*$/ : /^-?[\d\s ]*$/;

  const stepBtn = (dir: 1 | -1, icon: ReactNode) => (
    <button
      type="button"
      tabIndex={-1}
      aria-label={dir > 0 ? ui.increase : ui.decrease}
      disabled={props.disabled || (dir < 0 ? min !== undefined && (value ?? 0) <= min : max !== undefined && (value ?? 0) >= max)}
      onClick={() => bump(dir)}
      className={cn("grid aspect-square h-[calc(100%-8px)] max-h-8 place-items-center rounded-full hover:opacity-85 disabled:opacity-40 [&_svg]:size-5! [&_svg]:text-current!", dir > 0 ? "bg-fg text-surface" : "bg-disabled text-fg")}
    >
      {icon}
    </button>
  );

  return (
    <Input
      {...props}
      size={size}
      inputMode={precision > 0 ? "decimal" : "numeric"}
      value={text}
      className={cn(stepper && "text-center tabular-nums", "tabular-nums", className)}
      wrapperClassName={cn(stepper && "px-1", props.wrapperClassName)}
      leftIcon={stepper ? stepBtn(-1, <Minus />) : props.leftIcon}
      rightIcon={stepper ? stepBtn(1, <Plus />) : props.rightIcon}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onChange={(e) => {
        if (!pattern.test(e.target.value)) return;
        setText(e.target.value);
        onValueChange(parseDecimal(e.target.value));
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp") { e.preventDefault(); bump(1); }
        if (e.key === "ArrowDown") { e.preventDefault(); bump(-1); }
        props.onKeyDown?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        commit(parseDecimal(text));
        onBlur?.(e);
      }}
    />
  );
}

export type MoneyInputProps = Omit<NumberInputProps, "value" | "onValueChange" | "precision" | "step"> & {
  /** Сумма в minor units (тыйын/копейки). */
  value: number | null;
  onValueChange: (minor: number | null) => void;
  currencySymbol?: string;
  fractionDigits?: number;
};

export function MoneyInput({ value, onValueChange, currencySymbol = getUiLocale().currencySymbols.KGS, fractionDigits = 2, min = 0, ...props }: MoneyInputProps) {
  const k = 10 ** fractionDigits;
  return (
    <NumberInput
      {...props}
      min={min === undefined ? undefined : min / k}
      max={props.max === undefined ? undefined : props.max / k}
      precision={fractionDigits}
      suffix={props.suffix ?? currencySymbol}
      value={value === null ? null : value / k}
      onValueChange={(n) => onValueChange(n === null ? null : Math.round(n * k))}
    />
  );
}
